import { useState } from "react";
import { toast } from "sonner";
import { Music, Mic, ArrowRight, CheckCircle2, Upload } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_MB = 25;

export default function JoinTheLabel() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    ig_handle: "",
    soundcloud_url: "",
    notes: "",
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { submission_id, message }

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      setFile(null);
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Track is too big (${(f.size / 1024 / 1024).toFixed(1)} MB). Max is ${MAX_MB} MB.`);
      e.target.value = "";
      setFile(null);
      return;
    }
    setFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("mp3", file);

    try {
      const res = await fetch(`${BACKEND}/api/label/submit`, {
        method: "POST",
        body: fd,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.detail || `Submission failed (${res.status}).`);
      }
      setDone(body);
      toast.success("Submission received — check your email for confirmation.");
    } catch (err) {
      toast.error(err.message || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return <ThankYou id={done.submission_id} />;
  }

  return (
    <div className="min-h-screen bg-bdu-obsidian text-bdu-cream" data-testid="join-the-label-page">
      <Hero />
      <div className="max-w-3xl mx-auto px-6 md:px-8 py-16" data-testid="join-form-section">
        <div className="text-center mb-10">
          <div className="label-eyebrow text-bdu-gold mb-3">The Submission</div>
          <h2 className="font-serif text-3xl md:text-4xl leading-tight">Drop your best track. We'll do the rest.</h2>
          <p className="mt-3 text-bdu-cream/60 text-sm max-w-xl mx-auto">
            Greg L. Bradford and Shadow Soulja Muziq personally review every submission. If your sound moves us, we move fast.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-6" data-testid="join-form">
          <Field label="Your Name" required>
            <input
              type="text"
              required
              value={form.name}
              onChange={update("name")}
              className="bdu-input"
              placeholder="Artist / stage name"
              data-testid="join-name-input"
              maxLength={100}
            />
          </Field>

          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Email" required>
              <input
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                className="bdu-input"
                placeholder="you@youremail.com"
                data-testid="join-email-input"
                maxLength={200}
              />
            </Field>
            <Field label="Phone">
              <input
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                className="bdu-input"
                placeholder="+1 (555) 000-0000"
                data-testid="join-phone-input"
                maxLength={40}
              />
            </Field>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Instagram handle">
              <input
                type="text"
                value={form.ig_handle}
                onChange={update("ig_handle")}
                className="bdu-input"
                placeholder="@yourhandle"
                data-testid="join-ig-input"
                maxLength={60}
              />
            </Field>
            <Field label="SoundCloud / streaming link">
              <input
                type="url"
                value={form.soundcloud_url}
                onChange={update("soundcloud_url")}
                className="bdu-input"
                placeholder="https://soundcloud.com/..."
                data-testid="join-soundcloud-input"
                maxLength={400}
              />
            </Field>
          </div>

          <Field label="Your track (MP3 · max 25 MB)">
            <label
              htmlFor="join-mp3-input"
              className="block border border-dashed border-bdu-gold/40 bg-bdu-surface hover:border-bdu-gold/70 transition-colors cursor-pointer p-6 text-center"
              data-testid="join-mp3-dropzone"
            >
              {file ? (
                <div className="flex items-center justify-center gap-3 text-bdu-gold">
                  <Music size={20} />
                  <span data-testid="join-mp3-filename">{file.name}</span>
                  <span className="text-bdu-cream/50 text-xs">· {(file.size / 1024 / 1024).toFixed(1)} MB</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 text-bdu-cream/60">
                  <Upload size={22} />
                  <span className="text-sm">Tap to upload your MP3, M4A, WAV or AAC</span>
                  <span className="text-xs text-bdu-cream/40">One track only — pick your best one</span>
                </div>
              )}
              <input
                id="join-mp3-input"
                type="file"
                accept="audio/mpeg,audio/mp3,audio/x-m4a,audio/mp4,audio/wav,audio/aac,.mp3,.m4a,.wav,.aac"
                onChange={onFile}
                className="hidden"
                data-testid="join-mp3-input"
              />
            </label>
          </Field>

          <Field label="Anything you want us to know?">
            <textarea
              rows={4}
              value={form.notes}
              onChange={update("notes")}
              className="input-bdu resize-none"
              placeholder="Who are you? What's your sound? What are you looking for?"
              data-testid="join-notes-input"
              maxLength={1500}
            />
          </Field>

          <button
            type="submit"
            disabled={submitting}
            className="btn-gold w-full text-sm inline-flex items-center justify-center gap-2 py-4 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="join-submit-button"
          >
            {submitting ? (
              "Submitting…"
            ) : (
              <>
                <Mic size={16} /> SUBMIT MY MUSIC <ArrowRight size={16} />
              </>
            )}
          </button>

          <p className="text-xs text-bdu-cream/40 text-center mt-4">
            By submitting you agree Rise II Power may review your track for signing consideration. We will not
            share your track publicly without your written consent.
          </p>
        </form>
      </div>

      <FoundersStrip />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-bdu-gold/15" data-testid="join-hero">
      <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 30% 20%, rgba(238,204,120,0.35), transparent 60%), radial-gradient(circle at 80% 80%, rgba(227,38,54,0.25), transparent 55%)" }} />
      <div className="relative max-w-4xl mx-auto px-6 md:px-8 pt-24 pb-20 text-center">
        <div className="label-eyebrow text-bdu-gold mb-4">Rise II Power Music Empire</div>
        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05]">
          The hottest label<br />
          <span className="text-bdu-gold">is looking for you.</span>
        </h1>
        <p className="mt-6 max-w-2xl mx-auto text-bdu-cream/70 text-base md:text-lg leading-relaxed">
          Drop your best track. Greg L. Bradford (Founder · CEO · President) and Shadow Soulja Muziq
          (Founder · CEO · President · SEKHEM RE) hand-review every submission. If your sound moves us,
          we move fast.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-xs text-bdu-cream/50">
          <span>7-day personal reply</span>
          <span className="text-bdu-gold/60">·</span>
          <span>MP3 / M4A / WAV up to 25 MB</span>
          <span className="text-bdu-gold/60">·</span>
          <span>Free to submit</span>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.2em] text-bdu-gold/80 mb-2">
        {label}
        {required && <span className="text-bdu-red ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function FoundersStrip() {
  const founders = [
    { name: "Greg L. Bradford", title: "Founder · CEO · President", brand: "Rise II Power Music Empire" },
    { name: "Shadow Soulja Muziq", title: "Founder · CEO · President", brand: "SEKHEM RE" },
  ];
  return (
    <section className="border-t border-bdu-gold/15 bg-bdu-surface/40" data-testid="join-founders-strip">
      <div className="max-w-4xl mx-auto px-6 md:px-8 py-14">
        <div className="text-center mb-8">
          <div className="label-eyebrow text-bdu-gold mb-2">Who's listening</div>
          <h3 className="font-serif text-2xl md:text-3xl">Every track lands on their desk.</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {founders.map((f) => (
            <div key={f.name} className="border border-bdu-gold/20 bg-black/40 p-6" data-testid={`join-founder-${f.name.split(" ")[0].toLowerCase()}`}>
              <div className="text-bdu-cream font-serif text-2xl">{f.name}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-bdu-gold/70 mt-2">{f.title}</div>
              <div className="text-sm text-bdu-cream/70 mt-1">{f.brand}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ThankYou({ id }) {
  return (
    <div className="min-h-screen bg-bdu-obsidian text-bdu-cream flex items-center justify-center px-6" data-testid="join-thank-you">
      <div className="max-w-xl text-center">
        <CheckCircle2 className="mx-auto text-bdu-gold" size={64} />
        <h1 className="mt-6 font-serif text-4xl md:text-5xl">You're in the queue.</h1>
        <p className="mt-4 text-bdu-cream/70 leading-relaxed">
          Your submission is with Greg + the Rise II Power A&amp;R team. Expect a personal reply within 7
          business days — check your email for a confirmation from us.
        </p>
        <div className="mt-6 inline-block border border-bdu-gold/25 px-5 py-3 text-xs uppercase tracking-[0.2em] text-bdu-cream/60" data-testid="join-thank-you-id">
          Confirmation · <span className="text-bdu-gold font-mono normal-case tracking-normal">{id}</span>
        </div>
        <div className="mt-10">
          <a href="/" className="btn-ghost text-xs inline-flex items-center gap-2 px-6 py-3" data-testid="join-back-home">
            Back to Black Dollars United
          </a>
        </div>
      </div>
    </div>
  );
}
